import { ReactNode, useEffect, useRef, useState } from "react";
import { Box, Container, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBrandLine,
  colorLandingInk,
  colorLandingLine,
  colorLandingMuted,
  colorLandingSurface,
  colorLandingTint,
  radiusLandingPanel,
  shadowLandingFlat,
} from "src/design/tokens";
import { SectionHeading } from "./components/SectionHeading";
import { FeatureCheckList } from "./components/FeatureCheckList";
import { ShowcaseTab } from "./components/ShowcaseTab";
import { BookingsMedia } from "./components/showcase/BookingsMedia";
import { OffPeakMedia } from "./components/showcase/OffPeakMedia";
import { FloorPlanMedia } from "./components/showcase/FloorPlanMedia";
import { AnalyticsMedia } from "./components/showcase/AnalyticsMedia";
import { MenuMedia } from "./components/showcase/MenuMedia";
import { GalleryMedia } from "./components/showcase/GalleryMedia";
import { TeamMedia } from "./components/showcase/TeamMedia";
import {
  AnalyticsIcon,
  BookingsIcon,
  FloorPlanIcon,
  GalleryIcon,
  MenuIcon,
  OffPeakIcon,
  TeamIcon,
} from "./components/showcase/ShowcaseIcons";

const ROTATION_MS = 5000;

interface ShowcaseFeature {
  /** i18n namespace under `landingPage.showcase` holding this tab's copy. */
  key: string;
  icon: ReactNode;
  media: ReactNode;
  /** Keys under `<namespace>.checks`, in display order. */
  checks: string[];
}

const FEATURES: ShowcaseFeature[] = [
  {
    key: "bookings",
    icon: <BookingsIcon />,
    media: <BookingsMedia />,
    checks: ["whatsapp", "blacklist", "statuses", "views"],
  },
  {
    key: "offPeak",
    icon: <OffPeakIcon />,
    media: <OffPeakMedia />,
    checks: ["oneClick", "visualize", "impact"],
  },
  {
    key: "floorPlan",
    icon: <FloorPlanIcon />,
    media: <FloorPlanMedia />,
    checks: ["dragDrop", "noDoubleBooking", "unassigned"],
  },
  {
    key: "analytics",
    icon: <AnalyticsIcon />,
    media: <AnalyticsMedia />,
    checks: ["trend", "breakdown", "offerImpact"],
  },
  {
    key: "menu",
    icon: <MenuIcon />,
    media: <MenuMedia />,
    checks: ["editable", "signature", "qrCode", "published"],
  },
  {
    key: "gallery",
    icon: <GalleryIcon />,
    media: <GalleryMedia />,
    checks: ["galleries", "storefront", "seasonal"],
  },
  {
    key: "team",
    icon: <TeamIcon />,
    media: <TeamMedia />,
    checks: ["roles", "switch", "centralized"],
  },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The feature tour: a rail of tabs beside one panel at a time. The panels
 * auto-advance every 5s, pause while the pointer is over the showcase, and
 * stop for good once the visitor picks a tab themselves.
 */
export const FeaturesShowcaseSection = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [manuallySelected, setManuallySelected] = useState(false);
  const reducedMotion = useRef(prefersReducedMotion());

  const autoRotating = !manuallySelected && !hovered && !reducedMotion.current;

  useEffect(() => {
    if (!autoRotating) return undefined;
    const timer = window.setInterval(
      () => setActiveIndex((current) => (current + 1) % FEATURES.length),
      ROTATION_MS
    );
    return () => window.clearInterval(timer);
  }, [autoRotating]);

  const active = FEATURES[activeIndex];
  const namespace = `landingPage.showcase.${active.key}`;

  return (
    <Box
      component="section"
      id="fonctionnalites"
      sx={{ py: { xs: 8, md: 11.5 }, borderTop: `1px solid ${colorLandingLine}` }}
    >
      <Container maxWidth="lg">
        <SectionHeading
          title={t("landingPage.showcase.title")}
          subtitle={t("landingPage.showcase.subtitle")}
        />

        <Grid
          container
          spacing={3}
          sx={{ mt: 4 }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <Grid item xs={12} md={4}>
            <Box
              role="tablist"
              sx={{
                display: "flex",
                flexDirection: { xs: "row", sm: "column" },
                flexWrap: { xs: "wrap", sm: "nowrap" },
                justifyContent: { xs: "center", sm: "flex-start" },
                gap: 1.25,
              }}
            >
              {FEATURES.map((feature, index) => (
                <ShowcaseTab
                  key={feature.key}
                  label={t(`landingPage.showcase.${feature.key}.tab`)}
                  icon={feature.icon}
                  active={index === activeIndex}
                  showProgress={autoRotating}
                  progressDurationMs={ROTATION_MS}
                  onSelect={() => {
                    setManuallySelected(true);
                    setActiveIndex(index);
                  }}
                />
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box
              role="tabpanel"
              sx={{
                backgroundColor: colorLandingSurface,
                border: `1px solid ${colorLandingLine}`,
                borderRadius: radiusLandingPanel,
                boxShadow: shadowLandingFlat,
                p: { xs: 2.5, md: 3.75 },
                minHeight: { md: 452 },
              }}
            >
              <Box
                key={active.key}
                sx={{
                  animation: "showcasePanelIn .35s ease",
                  "@keyframes showcasePanelIn": {
                    from: { opacity: 0, transform: "translateY(8px)" },
                    to: { opacity: 1, transform: "none" },
                  },
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}
              >
                <Box
                  sx={{
                    backgroundColor: colorLandingTint,
                    border: `1px solid ${colorLandingBrandLine}`,
                    borderRadius: "14px",
                    p: 2.25,
                    mb: 2.75,
                    minHeight: 184,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  {active.media}
                </Box>

                <Typography
                  component="h3"
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: colorLandingInk,
                    mb: 1.25,
                  }}
                >
                  {t(`${namespace}.title`)}
                </Typography>
                <Typography sx={{ color: colorLandingMuted, fontSize: 15, lineHeight: 1.6 }}>
                  {t(`${namespace}.description`)}
                </Typography>

                <FeatureCheckList
                  sx={{ mt: 2 }}
                  items={active.checks.map((check) =>
                    t(`${namespace}.checks.${check}`)
                  )}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
